import React from 'react';
import { Sparkles } from 'lucide-react';
import { getMediaUrl, MediaConfig } from '@musedrops/shared';
import { AudioFileField } from '../../../shared/components/forms/AudioFileField';

interface ThemeMusicSectionProps {
  showSlug: string;
  showId?: string;
  themeMusicUpdatedAt?: string | null;
  musicPrompt?: string | null;
  onThemeMusicDeleted?: () => void;
  // eslint-disable-next-line no-unused-vars
  onThemeMusicUpdatedAtChanged?: (timestamp: string) => void;
  // Generation controls (Issue #601)
  onGenerate?: () => Promise<void> | void;
  isGenerating?: boolean;
}

/**
 * Theme Music Section Component
 *
 * Wrapper around AudioFileField for show theme music (instrumental only).
 * Supports both upload and AI generation via Kie.ai (Issue #601).
 */
export const ThemeMusicSection: React.FC<ThemeMusicSectionProps> = ({
  showSlug,
  showId,
  themeMusicUpdatedAt,
  musicPrompt,
  onThemeMusicDeleted,
  onThemeMusicUpdatedAtChanged,
  onGenerate,
  isGenerating = false,
}) => {
  // Use conventional filename from MediaConfig
  const themeMusicFile = MediaConfig.show.themeMusic; // 'theme.mp3'

  // Construct audio URL with timestamp for cache-busting
  const audioUrl = React.useMemo(() => {
    if (showSlug && themeMusicUpdatedAt) {
      return `${getMediaUrl('shows', showSlug, themeMusicFile)}?t=${new Date(themeMusicUpdatedAt).getTime()}`;
    }
    if (showSlug) {
      return getMediaUrl('shows', showSlug, themeMusicFile);
    }
    return undefined;
  }, [showSlug, themeMusicUpdatedAt, themeMusicFile]);

  // Determine generate button tooltip based on state
  const getGenerateTitle = () => {
    if (!showId) {
      return 'Save show first to generate theme music';
    }
    if (!musicPrompt) {
      return 'Add a music prompt to generate theme music';
    }
    if (isGenerating) {
      return 'Generating theme music...';
    }
    return 'Generate theme music using AI (Kie.ai/Suno)';
  };

  // Can generate only if we have showId and musicPrompt (isGenerating handled by AudioFileField's disabled state)
  const canGenerate = !!showId && !!musicPrompt;

  return (
    <AudioFileField
      label="Theme Music"
      value={themeMusicUpdatedAt ? themeMusicFile : null} // Show filename if file exists (timestamp present)
      onUpload={() => {
        // Upload handled by AudioFileField, filename is conventional (theme.mp3)
        // No-op handler - just enables upload button. Timestamp update handled by onTimestampChanged.
      }}
      onDelete={onThemeMusicDeleted}
      onTimestampChanged={onThemeMusicUpdatedAtChanged}
      audioUrl={audioUrl}
      storageFolder="shows"
      slug={showSlug}
      entityId={showId}
      tableName="shows"
      fileType="themeMusic"
      accept="audio/*"
      uploadTitle="Upload Theme Music"
      // Generation controls (Issue #601)
      // Always pass onRegenerate so button shows with spinner during generation
      showGenerate={!!onGenerate}
      onRegenerate={canGenerate ? onGenerate : undefined}
      isRegenerating={isGenerating}
      regenerateTitle={getGenerateTitle()}
      RegenerateIcon={Sparkles as React.ComponentType<{ size?: number }>}
      disabled={!showId}
    />
  );
};
